/**
 * 下注成功与失败的反馈。视觉规格 §10.3。
 *
 * <p><b>为什么单独写一个模块</b>：§10.3 规定的三件事（成功 toast、滚到我的票、
 * 失败提示）一件都没实现过，而失败路径只渲染 `MILHAR_ROUND_RISK_LIMIT` 一种码，
 * 其余全被吞掉。结果是**下注成功和下注失败在屏幕上长得一模一样**：号被清空，
 * 别的什么都没有。玩家会以为没点上，然后重复点击 —— 重复下注是这个洞真正的
 * 代价，不只是「体验不好」。
 */

export type MilharBetErrorKey =
  | 'milharErrRiskLimit'
  | 'milharErrBalance'
  | 'milharErrRoundLocked'
  | 'milharErrPrevDrawing'
  | 'milharErrInvalidPick'
  | 'milharErrUnavailable'
  | 'milharErrGeneric';

/**
 * 后端错误码 → i18n key。
 *
 * <p><b>永远返回一个 key，绝不把原始码丢给玩家看</b>。未知码落到
 * `milharErrGeneric`（「没成功，再试一次」）—— 玩家看不懂 `NO_OPEN_ROUND`，
 * 而看到一串大写英文只会以为是崩溃。
 */
export function milharBetErrorKey(code: string | null | undefined): MilharBetErrorKey {
  switch ((code || '').trim()) {
    case 'MILHAR_ROUND_RISK_LIMIT':
      return 'milharErrRiskLimit';
    case 'INSUFFICIENT_BALANCE':
    case 'INSUFFICIENT_AVAILABLE_BALANCE':
      return 'milharErrBalance';
    case 'ROUND_LOCKED':
    case 'NO_OPEN_ROUND':
      return 'milharErrRoundLocked';
    // 「上一期还在开」和「这期封盘了」是两回事：前者等几秒就能下，后者要等下一期。
    // 混成同一句话，玩家会以为自己错过了这一期而去追下一期 —— 正好是这条规则要防的。
    case 'PREV_ROUND_DRAWING':
      return 'milharErrPrevDrawing';
    case 'MILHAR_INVALID_PICK':
      return 'milharErrInvalidPick';
    case 'MILHAR_NOT_AVAILABLE':
    case 'MILHAR_TIER_NOT_AVAILABLE':
    case 'MILHAR_DISABLED':
      return 'milharErrUnavailable';
    default:
      return 'milharErrGeneric';
  }
}

/**
 * 成功 toast 的参数。§10.3 的原文是 `Aposta confirmada · 1208 · 10 rodadas`。
 *
 * <p>期数为 1 时**不带期数那一段** —— 「· 1 rodada」是废话，而 toast 的宽度
 * 有限，废话会把号码挤掉，号码才是玩家要确认的那个东西。
 */
export function betConfirmText(
  /** 单期文案，占位符 `%n%` */
  templateOne: string,
  /** 多期文案，占位符 `%n%` 与 `%r%`。这条 i18n key 早就存在（milharBetConfirmed），
   *  §10.3 的文案当初就写好了 —— 缺的一直只是把 toast 接上。 */
  templateWithRounds: string,
  pick: string,
  rounds: number,
): string {
  if (!Number.isFinite(rounds) || rounds <= 1) return templateOne.replace('%n%', pick);
  return templateWithRounds.replace('%n%', pick).replace('%r%', String(Math.floor(rounds)));
}
